import React, { ForwardRefExoticComponent, RefAttributes } from "react";
import { HeaderContext } from "@tanstack/react-table";
import { COLUMN_TYPE, HeaderIconType } from "@/types/shared";
import {
  Text,
  Hash,
  List,
  Clock,
  Braces,
  PenLine,
  Coins,
  Construction,
  LucideProps,
  AlertTriangle,
  Tag,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import HeaderWrapper from "@/components/shared/DataTableHeaders/HeaderWrapper";
import useSortableHeader from "@/components/shared/DataTableHeaders/useSortableHeader";
import ExplainerIcon from "@/components/shared/ExplainerIcon/ExplainerIcon";

const COLUMN_TYPE_MAP: Record<
  HeaderIconType,
  ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >
> = {
  guardrails: Construction,
  tags: Tag,
  [COLUMN_TYPE.string]: Text,
  [COLUMN_TYPE.number]: Hash,
  [COLUMN_TYPE.list]: List,
  [COLUMN_TYPE.time]: Clock,
  [COLUMN_TYPE.duration]: Clock,
  [COLUMN_TYPE.dictionary]: Braces,
  [COLUMN_TYPE.numberDictionary]: PenLine,
  [COLUMN_TYPE.cost]: Coins,
  [COLUMN_TYPE.category]: Text,
  [COLUMN_TYPE.errors]: AlertTriangle,
};

const TypeHeader = <TData,>(context: HeaderContext<TData, unknown>) => {
  const { column } = context;
  const {
    header,
    headerCheckbox,
    type: columnType,
    iconType,
    explainer,
  } = column.columnDef.meta ?? {};

  const type = iconType ?? columnType;
  const Icon = type ? COLUMN_TYPE_MAP[type] : "span";

  const { className, onClickHandler, renderSort } = useSortableHeader({
    column,
    withSeparator: Boolean(explainer),
  });

  return (
    <HeaderWrapper
      metadata={context.column.columnDef.meta}
      tableMetadata={context.table.options.meta}
      className={className}
      onClick={onClickHandler}
    >
      {headerCheckbox && (
        <Checkbox
          className="mr-3.5"
          onClick={(event) => event.stopPropagation()}
          checked={
            context.table.getIsAllPageRowsSelected() ||
            (context.table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) =>
            context.table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
        />
      )}
      {Boolean(Icon) && <Icon className="size-3.5 shrink-0 text-light-slate" />}
      <span className="truncate">{header}</span>
      {explainer && <ExplainerIcon {...explainer} />}
      {renderSort()}
    </HeaderWrapper>
  );
};

export default TypeHeader;
